import { mapGetters, mapMutations } from 'vuex';
import Forms from './../../utils/forms/forms';
import { sortBatchOptions, defaultBatchId } from './../../utils/batchOptions';
import productService from './../../services/product';
import batchService from './../../services/batch';
import taxService from './../../services/tax';
import orderProductService from './../../services/orderProduct';
import unitTypeService from './../../services/unitType';
import productDepositService from './../../services/productDeposit';
import store from './../../store';
import QuickProductEdit from './../../components/quickProductEdit/quickProductEdit.vue';
import warehouseService from './../../services/warehouse';
import organizationSettingsService from './../../services/organizationSettings';
import bulkAddProductModal from './../../components/bulkAddProductModal/bulkAddProductModal.vue';
import orderWarehousePopup from './../../components/orderWarehousePopup/orderWarehousePopup.vue';

export default {
  data() {
    return {
      form: new Forms({
        id: {
          value: '',
          type: 'text',
        },
        name: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        note: {
          value: '',
          type: 'text',
        },
        batchIds: {
          value: [],
          type: 'select',
        },
        transactions: {
          value: '',
          type: 'plaintext',
        },
        available: {
          value: '',
          type: 'plaintext',
        },
        price: {
          value: '',
          type: 'float',
        },
        productDepositName: {
          value: '',
          type: 'plaintext',
        },
        unitTypeId: {
          value: '',
          type: 'select',
        },
        unitQuantity: {
          value: '0',
          type: 'float',
        },
        quantity: {
          value: '0',
          type: 'float',
        },
        discount: {
          value: '',
          type: 'number',
        },
        taxId: {
          value: '',
          type: 'select',
        },
      }),
      hiddenFields: ['id'],
      additionalButtons: [{
        text: this.$t('table.orderProducts.addProductsButton'),
        action: this.showBulkAddProductModal,
      }],
      linesLoading: true,
    };
  },

  props: {
    /**
     * The current order.
     */
    order: {
      type: Object,
      required: true,
    },

    /**
     * The current order.
     */
    billRoundInvoiceTotal: {
      type: Boolean,
      required: false,
    },
  },

  computed: {
    ...mapGetters({
      taxes: 'allTaxes',
      orderProducts: 'allOrderProducts',
      productDeposits: 'allProductDeposits',
      account: 'account',
      settings: 'organizationSettings',
      batches: 'allBatches',
      currentOrder: 'currentOrder',
      unitTypes: 'allUnitTypes',
    }),

    checkHasInventory() {
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Inventory') {
            return true;
          }
        }
      }

      return false;
    },

    hasProduction() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'Production');
      }
      if (this.account.allowProducerBatches) return true;
      return false;
    },

    list() {
      return this.orderProducts.map((p) => {
        let deposit = [];
        if (p.productDepositId) {
          deposit = this.productDeposits.all
            .filter(d => p.productDepositId.toString() === d.id.toString());
        }

        let value = '';
        if (deposit.length) {
          value = `${this.$t('static.orders.edit.deposit')}: ${deposit[0].name}`;
        }

        if (!this.checkHasInventory) {
          this.form.removeField('available');
        }

        const nonBatchProduct = p.warehouses.filter(p => p.warehouseId === this.order.warehouseId.toString()).find(p => p.batchId === 0);
        let batchIds = [];
        if (p.batchIds.length) {
          batchIds = batchIds.concat(p.batchIds.map(id => id.toString()));
        } else if (nonBatchProduct) {
          batchIds = ['0'];
        }

        const transactions = p.warehouseTransactions.map((t) => {
          const batchObj = t.batch_id ? this.batches.find(b => +b.id === +t.batch_id) : '';
          const batch = batchObj ? batchObj.code : `${this.$t('static.orders.edit.nonBatch')}`;
          return `${batch} ( qty: ${t.adjustment_qty} )`;
        });

        const row = {
          batchIds,
          transactions: transactions ? transactions.join(', ') : '',
          productDepositName: value,
          price: p.price.toString().replace(/\./g, ','),
        };
        if (p.taxPercentage !== undefined && p.taxPercentage !== null) {
          row.taxIdDisplay = p.taxPercentage;
        }
        return Object.assign({}, p, row);
      });
    },

    totalsTableList() {
      const fmt = val => Number(val).toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const result = [
        {
          name: this.$t('static.orders.edit.subtotal'),
          value: fmt(this.subtotal),
        },
        {
          name: this.$t('static.orders.edit.tax'),
          value: fmt(this.tax),
        },
      ];

      if (this.discount > 0) {
        result.push({
          name: this.$t('static.orders.edit.discount'),
          value: fmt(this.discount),
        });
      }

      if (this.deposit > 0) {
        result.push({
          name: this.$t('static.orders.edit.deposit'),
          value: fmt(this.deposit),
        });
      }

      result.push({
        name: this.$t('static.orders.edit.total'),
        value: fmt(this.total),
      });

      return result;
    },

    /**
     * Whether table was shown in a bill and round invoice total is true
     */
    isShownOnBill() {
      if (this.$route.name === 'bills.create') {
        return this.order.roundInvoiceTotal;
      }

      return this.billRoundInvoiceTotal;
    },

    subtotal() {
      return this.orderProducts.map(p => (
        p.price * p.quantity
      )).reduce((a, b) => a + b, 0).toFixed(2);
    },

    discount() {
      return this.orderProducts.map(p => p.price * p.quantity * (p.discount / 100))
        .reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    tax() {
      return this.orderProducts.map(p => (
          p.price * p.quantity * (1 - (p.discount / 100)) * (p.taxPercentage / 100)
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    deposit() {
      return this.orderProducts.map(p => (
          p.productDeposit * p.quantity
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    total() {
      const total = Number(this.subtotal - this.discount);
      const totalTax = Number(this.deposit) + total + Number(this.tax);

      if (this.isShownOnBill) {
        return Math.round(totalTax);
      }

      return totalTax.toFixed(2);
    },

    /**
     * The rounding for the total.
     */
    rounding() {
      const total = Number(this.subtotal - this.discount);
      const totalTax = total + Number(this.tax);
      const roundedDecimal = (totalTax % 1).toFixed(2);

      if (roundedDecimal < 0.50) {
        return -roundedDecimal;
      }

      return roundedDecimal;
    },
  },

  watch: {
    /**
     * Fetch order products whenever order is ready.
     */
    order() {
      this.getResources();
    },

    currentOrder(currentOrder) {
      if (currentOrder.orderStatus !== 'created') {
        this.hiddenFields.push('available');
      }

      if (currentOrder.orderStatus === 'confirmed' || currentOrder.orderStatus === 'shipping_confirmed') {
        this.additionalButtons = [];
      }
    },

    orderProducts(orderProducts) {
      this.configFieldVisibility();
      const beverageProducts = orderProducts.filter(p => p.productType === 'Beverage' || p.productTypeGroup === 'Beverage');
      if (!beverageProducts.length) {
        this.hiddenFields.push('unitTypeId');
        this.hiddenFields.push('unitQuantity');
        this.form.enableField('quantity');
      } else if (this.hiddenFields.indexOf('unitTypeId') !== -1) {
        this.hiddenFields.splice(this.hiddenFields.indexOf('unitTypeId'), 1);
        this.hiddenFields.splice(this.hiddenFields.indexOf('unitQuantity'), 1);
        this.form.disableField('quantity');
      }

      if (this.order.client.clientType.id === 11) {
        this.hiddenFields.push('taxId');
      }
    },

    unitTypes(unitTypes) {
      this.form.setOptions('unitTypeId', unitTypes.all.map(item => ({
        id: item.id.toString(),
        name: item.name,
      })));
    },

    /**
     * Watch product select for autocomplete other fields with defaults.
     */
    'form.name': function () {
      if (typeof this.form.name === 'object') {
        const tax = this.taxes.all.filter(t => (
          t.taxPercentage === this.form.name.taxPercentage
        ));
        if (tax.length > 0) {
          this.form.taxId = tax[0].id;
        }
        if (typeof this.form.name.options === 'object') {
          this.form.price = this.getPriceByClientType(this.form.name.options);
        } else {
          this.form.price = this.getPriceByClientType(this.form.name);
        }
        // console.log(this.form.name);
        this.form.note = this.form.name.orderProductNote;
        this.form.id = this.form.name.id;
      }
    },

    'form.id': function (id) {
      if (id && this.hasProduction) {
        if (+this.$refs.formTable.$data.editingItem === +id) {
          if (this.checkHasInventory) {
            if (!this.currentOrder.warehouseId && this.currentOrder.orderStatus !== 'cancelled') {
              this.$refs.formTable.closeForm();
              this.activateWarehouseModal();
              return;
            }
          }
        }

        let orderProduct = this.list.find(item => item.id === id);

        if (!orderProduct && typeof this.form.name === 'object') {
          orderProduct = this.form.name;
        }

        if (orderProduct && orderProduct.warehouses) {
          let products = orderProduct.warehouses;
          if (products.length) {
            if (this.currentOrder.warehouseId) {
              products = products.filter(p => p.warehouseId === this.currentOrder.warehouseId.toString());
            }

            const batchIds = products.map(p => p.batchId.toString());

            let options = [];
            options = options.concat(this.batches.filter(b => (batchIds.indexOf(b.id) !== -1)).map((batch) => {
              const batchProduct = products.find(p => p.batchId.toString() === batch.id.toString());
              return Object.assign({}, {
                id: batch.id.toString(),
                name: this.currentOrder.warehouseId ? `${batch.code} | ${this.$t('static.orders.edit.availableQty')}: ${batchProduct.qtyNumber}` : batch.code,
                available: batchProduct.qtyNumber,
              });
            }));
            options = options.reverse();

            const nonBatchProduct = products.find(p => p.batchId === 0);
            if (nonBatchProduct) {
              options.unshift({
                id: '0',
                name: `${this.$t('static.orders.edit.nonBatch')} | ${this.$t('static.orders.edit.availableQty')}: ${nonBatchProduct.qtyNumber}`,
                available: nonBatchProduct.qtyNumber,
              });
            }

            // Stock is only known per warehouse; without one, keep the list as is.
            if (this.currentOrder.warehouseId) {
              options = sortBatchOptions(options, { hideEmpty: true, keepId: this.form.batchId });
            }

            this.form.setOverrideOptions('batchIds', options);
            if (!this.form.batchId && batchIds.length) {
              this.form.batchId = this.currentOrder.warehouseId ? defaultBatchId(options) : batchIds[0];
            }
          } else {
            this.form.batchId = '';
            this.form.setOverrideOptions('batchIds', []);
          }
        } else {
          this.form.batchId = '';
          this.form.setOverrideOptions('batchIds', []);
        }
      }
      if (id) {
        let orderProduct = this.list.find(item => item.id === id);

        if (!orderProduct && typeof this.form.name === 'object') {
          orderProduct = this.form.name;
        }
        if (this.order.orderStatus === 'created') {
          if (orderProduct && orderProduct.productTypeGroup != 'Beverage') {
            this.form.disableField('unitTypeId');
            this.form.disableField('unitQuantity');
            this.form.enableField('quantity');
          } else {
            this.form.enableField('unitTypeId');
            this.form.enableField('unitQuantity');
            this.form.disableField('quantity');
          }
        }
      }
    },

    batches(batches) {
      const options = batches.map(batch => ({
        id: +batch.id,
        name: batch.code,
      }));
      this.form.setOptions('batchIds', [{
        id: 0,
        name: `${this.$t('static.orders.edit.nonBatch')}`,
      }, ...options]);
    },

    'form.batchIds': function (batchIds) {
      if (this.hasProduction) {
        let orderProduct = this.list.find(item => item.id === this.form.id);
        if (!orderProduct && typeof this.form.name === 'object') {
          orderProduct = this.form.name;
        }
        if (orderProduct) {
          let products = orderProduct.warehouses;
          if (this.order.warehouseId) {
            products = products.filter(p => p.warehouseId === this.order.warehouseId.toString());
          }
          const batchProducts = products.filter(p => batchIds.includes(p.batchId.toString()));
          let available = 0;
          if (batchProducts.length) {
            available = batchProducts.map(item => item.qtyNumber).reduce((total, qtyNumber) => total + qtyNumber);
          }
          this.form.available = available.toString();
        }
      }
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} taxes    The taxes list.
     */
    taxes(taxes) {
      this.form.setOptions('taxId', taxes.all.map(tax => ({ id: tax.id, name: tax.taxPercentage.toString() })));
    },
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalClass: 'SET_MODAL_CLASS',
    }),

    /**
     * Activates the modal
     */
    activateWarehouseModal() {
      this.setModalContent(() => orderWarehousePopup);
      this.setModalCloseOnClick(false);
      this.setModalShowClose(false);
      this.setModalCloseOnPressEscape(false);
      this.toggleModal(true);
    },

    configFieldVisibility() {
      if (!this.hasProduction) {
        this.hiddenFields = ['id', 'batchIds', 'transactions'];
      } else {
        this.hiddenFields = ['id'];
        if (this.order.orderStatus !== 'created') {
          this.hiddenFields.push('available');
          this.hiddenFields.push('batchIds');
        } else {
          this.hiddenFields.push('transactions');
        }
      }

      if (this.order.orderStatus !== 'created') {
        Object.keys(this.form.fields).forEach((field) => {
          if (field !== 'price') {
            this.form.disableField(field);
          }
        });
      }
    },

    getPriceByClientType(item) {
      if (!this.order.client.clientType.name.includes('vinmonopolet')) {
        return item.salePriceHoreca || item.price;
      }
      return item.ddpVmp || item.price;
    },

    toFloat(number) {
      return parseFloat(number.replace(/\s/g, '').replace(/,(\d+)$/, '.$1'));
    },

    showBulkAddProductModal() {
      if (this.hasProduction && this.checkHasInventory) {
        if (!this.currentOrder.warehouseId && this.currentOrder.orderStatus !== 'cancelled') {
          this.$refs.formTable.closeForm();
          this.activateWarehouseModal();
          return;
        }
      }
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        isOrderPage: true,
        warehouseId: this.order.warehouseId,
        batches: this.batches,
        fetchProductsMethod: this.fetchAllProductsBasic,
        onSave: this.handleBulkProductSave,
      });
      this.toggleModal(true);
    },

    fetchAllProductsBasic() {
      return Promise.all([
        productService.allBasic({ filter: 'active', include: 'warehouses' }),
      ]);
    },

    searchMethod(search) {
      return Promise.all([
        productService.search({ search, filter: 'active', warehouse_id: this.order.warehouseId }),
      ]);
    },

    handleBulkProductSave(selectedProducts) {
      const id = this.order.id;
      const data = selectedProducts.map(p => ({
        productId: p.id,
        name: p.name,
        quantity: p.qty,
        unitQuantity: p.unitQty,
        unitTypeId: p.unitTypeId.toString(),
        productDepositId: p.productDepositId,
        taxId: p.taxId,
        price: p.price,
        discount: 0,
        note: '',
        batchId: p.batchId || '',
      }));

      return orderProductService.bulkStore(id, data)
        .then(() => {
          this.form.loading = false;
        });
    },
    /**
     * The handler for the quick product edit component that will be passed to the modal.
     *
     * @param  {Object} item The row item.
     * @return {Object}      The component.
     */
    onQuickProductEditMounted() {
      const that = this;
      return function () {
        this.$once('close', () => {
          productService.show(this.orderProduct.productId).then((data) => {
            that.form.assignData(this.orderProduct);
            that.form.assignData(data);
            that.update(this.orderProduct.id).then(() => {
              that.form.resetValues();
            });
          });
        });
      };
    },


    updated(product, orderProduct) {
      this.form.assignData(orderProduct);
      this.form.assignData(product);
      return this.quickUpdateProduct(orderProduct.id, product);
      // console.log(productId, orderProduct);
      // orderProductService.all(this.order.id)
    },
    /**
     * Defines the handler for the links one the table.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or the handler function in case of a link needed.
     */
    tableClickHandler(item, name) {
      if (!this.order.sent && item.productId && name === 'name' && !item.bundleGroupId) {
        return () => {
          this.setModalProps({
            productId: item.productId,
            orderProduct: item,
            onMounted: this.onQuickProductEditMounted(),
            updated: this.updated,
          });
          this.setModalContent(() => QuickProductEdit);
          this.toggleModal(true);
        };
      }

      return false;
    },

    /**
     * Saves the form, adding a product.
     */
    save() {
      this.form.loading = true;

      const id = this.order.id;
      const data = this.form.data();

      if (data.name.id === undefined) {
        this.$message({
          showClose: true,
          customClass: 'error',
          message: this.$t('static.orders.orderTable.productErrorMessage'),
        });
        return Promise.all([]);
      }

      const selectedProduct = data.name;

      data.name = selectedProduct.name;
      data.productId = selectedProduct.id;
      data.productDepositId = selectedProduct.productDepositId;

      return new Promise((resolve, reject) => {
        orderProductService.store(id, data)
          .then(() => {
            orderProductService.all(this.order.id);
            this.form.loading = false;
            store.dispatch('addOrderProduct', data);
            resolve();
          }).catch((errors) => {
            this.form.loading = false;
            let message = '';
            if (errors.code) {
              message = errors.message;
            } else {
              this.form.recordErrors(errors);
              const errorsKeys = Object.keys(this.form.errors.errors);
              message = errors[errorsKeys[0]][0];
            }
            this.$message({
              showClose: true,
              customClass: 'error',
              message,
            });
            reject();
          });
      });
    },

    quickUpdateProduct(orderProductId, product) {
      this.form.loading = true;

      const id = this.order.id;
      const data = this.form.data();
      const selected = data.name;

      const new_product = [];

      data.name = typeof data.name === 'object' ? data.name.name : data.name;
      data.productDepositId = product.productDepositId ?
        product.productDepositId : null;

      return new Promise((resolve, reject) => {
        orderProductService.update(id, data, orderProductId)
          .then(() => {
            orderProductService.all(this.order.id);
            this.form.loading = false;
            store.dispatch('updateOrderProduct', { orderProduct: data, id: orderProductId });
            resolve();
          }).catch((errors) => {
            this.form.loading = false;
            this.form.recordErrors(errors);
            const errorsKeys = Object.keys(this.form.errors.errors);
            this.$message({
              showClose: true,
              message: errors[errorsKeys[0]][0],
              customClass: 'error',
            });
            reject();
          });
      });
    },

    /**
     * Updates a order product.
     */
    update(orderProductId) {
      this.form.loading = true;

      const id = this.order.id;
      const data = this.form.data();
      const selectedProduct = data.name;

      // means its a new selected product from autocomplete
      if (typeof selectedProduct === 'object') {
        data.name = selectedProduct.name;
        data.productId = selectedProduct.id;
        data.productDepositId = selectedProduct.productDepositId;
      } else {
        const orderProduct = this.orderProducts.find(op => +op.id === +orderProductId);
        data.name = orderProduct.name;
        data.productId = orderProduct.productId;
        data.productDepositId = orderProduct.productDepositId;
      }

      return new Promise((resolve, reject) => {
        orderProductService.update(id, data, orderProductId)
          .then(() => {
            orderProductService.all(this.order.id);
            this.form.loading = false;
            store.dispatch('updateOrderProduct', { orderProduct: data, id: orderProductId });
            resolve();
          }).catch((errors) => {
            this.form.loading = false;
            this.form.recordErrors(errors);
            const errorsKeys = Object.keys(this.form.errors.errors);
            this.$message({
              showClose: true,
              message: errors[errorsKeys[0]][0],
              customClass: 'error',
            });
            reject();
          });
      });
    },

    /**
     * Delete the product from order.
     */
    destroy(orderProductId) {
      return new Promise((resolve, reject) => {
        orderProductService.destroy(this.order.id, orderProductId)
          .then(() => {
            resolve();
          }).catch(() => {
            reject();
          });
      });
    },

    /**
     * Fetch the component's required resources.
     */
    getResources() {
      this.linesLoading = true;
      const resources = [
        taxService.all(),
        orderProductService.all(this.order.id),
        productDepositService.all(),
        unitTypeService.all(),
      ];
      if (this.hasProduction) {
        resources.push(batchService.all({
          warehouse_id: this.order.warehouseId,
          has_warehouse_transaction: 1,
          for_select: 1,
        }));
      }
      Promise.all(resources)
        .then(() => {
          this.linesLoading = false;
        })
        .catch(() => {
          this.linesLoading = false;
        });
    },
  },

  mounted() {
    this.getResources();
    Promise.all([organizationSettingsService.show()]).then((data) => {
      this.form.setDisplays('name', data[0].productInfoDisplay);
    });
    this.configFieldVisibility();
    this.form.setExtraAttr('batchIds', {
      multiple: true,
    });
    if (this.order.sent || this.order.orderStatus !== 'created') {
      this.additionalButtons = [];
    }
  },

  components: {
    VFormTable: require('./../formTable/formTable.vue'),
    VTotalsTable: require('./../totalsTable/totalsTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/orderProductsTable/orderProductsTable.js